/**
 * Brand Check Hooks
 *
 * React Query hooks for Brand Check feature
 */

import { useInfiniteQuery } from '@tanstack/react-query';
import { brandCheckService } from '@/services/brand-check.service';
import { BrandCheckSort, BrandCheckStatus } from '@/types/brand-check';
import { useEffectiveTeamId } from '@/hooks/useEffectiveTeamId';
import { teamQueryKeys } from '@/lib/queryKeys';

interface BrandCheckCalendarOptions {
  channelIds?: number[];  // Optional cross-filter
  status?: BrandCheckStatus;
  sort?: BrandCheckSort;
  search?: string;
  enabled?: boolean;
}

interface BrandCheckChannelOptions {
  calendarIds?: number[];  // Optional cross-filter
  status?: BrandCheckStatus;
  sort?: BrandCheckSort;
  search?: string;
  enabled?: boolean;
}

/**
 * Fetch brand check items by calendar (Calendar View)
 * Uses infinite scroll with cursor-based pagination
 */
export function useBrandCheckCalendar(
  calendarIds: number[],
  options?: BrandCheckCalendarOptions
) {
  const { teamId, hasTeam } = useEffectiveTeamId();

  return useInfiniteQuery({
    queryKey: [...teamQueryKeys.brandCheck.calendar(teamId), calendarIds, options],
    queryFn: ({ pageParam }) =>
      brandCheckService.getByCalendar({
        calendarIds,
        channelIds: options?.channelIds,
        status: options?.status,
        sort: options?.sort || 'oldest',
        search: options?.search,
        cursor: pageParam,
        limit: 50,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.pagination.nextCursor ?? undefined,
    enabled: hasTeam && calendarIds.length > 0 && (options?.enabled ?? true),
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}

/**
 * Fetch brand check items by channel (Channel View)
 * Uses infinite scroll with cursor-based pagination
 */
export function useBrandCheckChannel(
  channelIds: number[],
  options?: BrandCheckChannelOptions
) {
  const { teamId, hasTeam } = useEffectiveTeamId();

  return useInfiniteQuery({
    queryKey: [...teamQueryKeys.brandCheck.channel(teamId), channelIds, options],
    queryFn: ({ pageParam }) =>
      brandCheckService.getByChannel({
        channelIds,
        calendarIds: options?.calendarIds,
        status: options?.status,
        sort: options?.sort || 'newest',
        search: options?.search,
        cursor: pageParam,
        limit: 50,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.pagination.nextCursor ?? undefined,
    enabled: hasTeam && channelIds.length > 0 && (options?.enabled ?? true),
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}
